import React, { useEffect, useState } from "react";
import styles from "./institution-administrators.module.css";
import { Popconfirm, Typography } from "antd";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import SVGIcon from "../../../../constants/SVGIcon";
import CustomButton from "../../../../common/custom-button/custom-button";
import CustomTable from "../../../../common/custom-table/custom-table";
import AddInstitutionAdministrators from "./components/add-edit-institution-administrators/add-edit-institution-administrators";
import {
  deleteInstitutionAdmin,
  getInstitutionAdmins,
} from "../../../../services/admin";
import { toast } from "react-toastify";

const InstitutionAdministrators = ({ institutionId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [institutionAdminsList, setInstitutionAdminsList] = useState([]);
  const [selectedInstitutionAdminData, setSelectedInstitutionAdminData] =
    useState({});

  const columns = [
    {
      title: "Username",
      dataIndex: "user_name",
      key: "user_name",
      render: (value) => value || "-",
    },
    {
      title: "Admin Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (value) => value || "-",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (value) => value?.toUpperCase() || "-",
    },
    {
      title: "Email",
      dataIndex: "email_address",
      key: "email_address",
      render: (value) => value || "-",
    },
    {
      title: "Phone No.",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (value) => value || "-",
    },
  ];

  const menuItems = (record) => [
    {
      key: "1",
      label: "Update Details",
      onClick: () => handleUpdateAdmin(record),
    },
    {
      key: "2",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.first_name} ${record?.last_name}?`}
          onConfirm={() => handleDeleteAdmin(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span className={styles.full_width}>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  const handleUpdateAdmin = (data) => {
    setOpenDrawer(true);
    setSelectedInstitutionAdminData(data);
  };

  const handleDeleteAdmin = async (id) => {
    setLoading(true);
    try {
      await deleteInstitutionAdmin(id);
      toast.success("Institution Administrator Deleted Successfully!");
      getInstitutionAdminsList();
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "An error occured, please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPageNo(1);
      getInstitutionAdminsList(1);
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchTerm]);

  const getInstitutionAdminsList = async (
    tempPage = pageNo,
    tempPageSize = pageSize
  ) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        institution_id: institutionId,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }

      const response = await getInstitutionAdmins(payload);
      if (response?.data) {
        setInstitutionAdminsList(
          response?.data?.data?.admins?.map((item) => ({
            id: item?.id ?? "-",
            institution_id: item?.institution_id ?? "",
            user_name: item?.user_name ?? "",
            full_name: item?.metadata?.full_name ?? "",
            gender: item?.metadata?.gender ?? "",
            email_address: item?.email_address ?? "",
            date_of_birth: item?.metadata?.date_of_birth ?? "",
            full_address: item?.metadata?.full_address ?? "",
            phone_number: item?.metadata?.phone_number ?? "",
            privileges: item?.privileges ? JSON.parse(item?.privileges) : [],
            account_privileges: item?.account_privileges ? JSON.parse(item?.account_privileges) : [],
            designation: item?.designation ?? "",
          }))
        );
        setTotalCount(response?.data?.data?.totalRecords);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getInstitutionAdminsList(page, pageSize);
  };

  const handleAddNew = () => {
    setOpenDrawer(true);
  };

  const handleCloseAddNew = () => {
    setOpenDrawer(false);
    getInstitutionAdminsList();
    setSelectedInstitutionAdminData({});
  };

  return (
    <div className={styles.institutionadmnistrators}>
      <div className={styles.headerContainer}>
        <Typography className={styles.title}>School Administrators</Typography>
        <div className={styles.filterContainer}>
          <CustomTextfield
            id={"search"}
            type={"text"}
            className={styles.inputField}
            prefix={
              <SVGIcon
                name={"search-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#667085"}
              />
            }
            placeholder={"Search"}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            allowClear
          />
          <CustomButton
            title="Add Administrators"
            size={"large"}
            icon={
              <SVGIcon
                name={"add-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#FFF"}
              />
            }
            onClick={handleAddNew}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={institutionAdminsList}
          total={totalCount}
          loading={loading}
          showMoreIcon={true}
          menuItems={menuItems}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />

        {openDrawer && (
          <AddInstitutionAdministrators
            open={openDrawer}
            institutionId={institutionId}
            onClose={() => handleCloseAddNew()}
            selectedInstitutionAdminData={selectedInstitutionAdminData}
            setSelectedInstitutionAdminData={setSelectedInstitutionAdminData}
          />
        )}
      </div>
    </div>
  );
};

export default InstitutionAdministrators;
